import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail, setToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tv, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Public claim page — an invitee (channel exec) lands here from the
// invite email / SMS. No auth required. On successful accept, we log
// them in and route to the desk.
export default function AcceptShowDeskInvite() {
  const nav = useNavigate();
  const { token } = useParams();
  const [full, setFull] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!full.trim() || !password || password.length < 6) {
      toast.error("Full name + a password (min 6 chars) are required");
      return;
    }
    setSaving(true);
    try {
      const body = { full_name: full.trim(), password };
      if (email.trim()) body.email = email.trim();
      if (phone.trim()) body.phone = phone.trim();
      const { data } = await api.post(
        `/show-desks/invites/${token}/accept`, body);
      // Log them in
      const loginEmail = (email.trim() || "").toLowerCase();
      if (loginEmail) {
        const { data: auth } = await api.post("/auth/login",
          { email: loginEmail, password });
        setToken(auth.access_token);
      }
      toast.success("You're on the desk");
      // Full reload so AuthContext bootstraps from the new token
      window.location.assign(`/channel/show-desks/${data.desk_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not accept");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg px-4 sm:px-6 lg:px-8 py-16"
      data-testid="accept-invite-page">
      <div className="cs-card rounded-2xl p-8">
        <div className="w-12 h-12 rounded-xl bg-amber-500/15 text-amber-300 flex items-center justify-center mb-4">
          <Tv size={22} />
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
          Show Desk Invitation
        </div>
        <h1 className="font-display text-3xl uppercase tracking-tight mt-2">
          Claim your seat
        </h1>
        <p className="mt-3 text-sm text-white/60">
          A Show Desk on Call·Sheet is a scoped read-mostly cubicle for one
          TV show your channel commissions. Set a password below to join.
        </p>

        <div className="mt-8 space-y-4">
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Full name *
            </Label>
            <Input value={full} onChange={(e) => setFull(e.target.value)}
              placeholder="Your name"
              className="mt-2 h-11 bg-black/40 border-white/15"
              data-testid="accept-name-input" autoFocus />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Corporate email
            </Label>
            <Input type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@channel.tv"
              className="mt-2 h-11 bg-black/40 border-white/15"
              data-testid="accept-email-input" />
            <div className="mt-1 text-[10px] text-white/45">
              Required if your invite came via SMS.
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Mobile (optional)
            </Label>
            <Input type="tel" value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              className="mt-2 h-11 bg-black/40 border-white/15"
              data-testid="accept-phone-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Password *
            </Label>
            <Input type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Set a password"
              className="mt-2 h-11 bg-black/40 border-white/15"
              data-testid="accept-password-input" />
            <div className="mt-1 text-[10px] text-white/45">
              Minimum 6 characters.
            </div>
          </div>
          <Button onClick={submit} disabled={saving}
            className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em] mt-2"
            data-testid="accept-submit-btn">
            <ShieldCheck size={13} className="mr-1.5" />
            {saving ? "Joining…" : "Join the desk"}
          </Button>
        </div>
      </div>
    </div>
  );
}
